/**
 * pageIntros.js — Single source of truth for every Ultra Admin page's
 * "what is this page for" explainer. Pages look up their entry by key and
 * pass it into <PageIntro ... />.
 *
 * Why a registry and not inline JSX?
 * - Ensures consistent bilingual wording across the panel.
 * - New pages get their intro by adding ONE entry here, not by editing each
 *   page component.
 * - Keeps page components focused on behavior, not documentation.
 *
 * Usage:
 *   import { getPageIntro } from '../lib/pageIntros';
 *   const intro = getPageIntro('organizer-products', language);
 *   <PageIntro {...intro} />
 */

const REGISTRY = {
  /* ── Daily Operation ─────────────────────────────────────────────── */
  'dashboard': {
    tr: {
      title: 'Ultra Admin Panosu',
      description:
        'Platformun güncel durumunu tek ekranda görün. KPI kartları, müşteri ve tedarikçi sayıları, son siparişler buradan izlenir. KPI kartına tıklamak ilgili yönetim sayfasına götürür.',
      tips: [
        'Overview / Tenants / Users sekmeleri buradaki kartlara bağlıdır.',
        'Başvurular, Değişiklik Talepleri ve Stand Tasarımları artık sol menüden deep-link olarak açılır.',
      ],
    },
    en: {
      title: 'Ultra Admin Dashboard',
      description:
        'See the platform\'s live state at a glance. KPI cards, tenant and supplier counts, and recent orders surface here. Click any KPI to jump into the matching management page.',
      tips: [
        'Overview / Tenants / Users tabs mirror the KPI cards above.',
        'Applications, Change Requests and Stand Designs now deep-link from the left sidebar.',
      ],
    },
  },

  'tenants': {
    tr: {
      title: 'Tenant Yönetimi',
      description:
        'Tüm müşteri (Blue Zone / Green Zone) ve tedarikçi tenant\'larını bu sayfadan yönetin. Tenant oluşturma, aktifleştirme, askıya alma ve silme işlemleri buradan yapılır. Silme işlemi Ultra Password gerektirir.',
      tips: [
        'Tenant silindiğinde o tenant\'a bağlı tüm kullanıcılar ve siparişler etkilenir. Önce bağlılıkları kontrol edin.',
        'Askıya alma (suspend) geri alınabilir ve kullanıcıları kilitleyerek hızlı erişim kısıtlar.',
      ],
    },
    en: {
      title: 'Tenant Management',
      description:
        'Manage every customer (Blue/Green Zone) and supplier tenant from this page. Create, activate, suspend or delete tenants here. Deleting a tenant requires the Ultra Password.',
      tips: [
        'Deleting a tenant cascades to users and orders — review dependencies first.',
        'Suspending is reversible and locks user access immediately.',
      ],
    },
  },

  'users': {
    tr: {
      title: 'Kullanıcı Yönetimi',
      description:
        'Platformdaki tüm rol tipleri (Ultra Admin, Customer Admin, Supplier Admin, Contractor, Blue Zone Participant) için kullanıcıları yönetin. Silme ve rol değiştirme için Ultra Password gerekir.',
      tips: [
        'Birincil Süper Admin (is_main_user=true) kullanıcısı asla silinemez ve yetkileri düşürülemez.',
        'Kullanıcı silindiğinde bağlı tenant\'ın diğer admin\'leri hâlâ giriş yapabilir.',
      ],
    },
    en: {
      title: 'User Management',
      description:
        'Manage users across every role (Ultra Admin, Customer Admin, Supplier Admin, Contractor, Blue Zone Participant). Delete / role-change operations require the Ultra Password.',
      tips: [
        'The Primary Super Admin (is_main_user=true) cannot be deleted or demoted.',
        'When a user is deleted, the tenant\'s other admins keep working.',
      ],
    },
  },

  'stand-requests': {
    tr: {
      title: 'Stand Talepleri',
      description:
        'Müşterilerin yaptığı stand alanı taleplerini inceleyin. Onay / red, konum atama ve alternatif önerme işlemleri bu sayfadan yapılır.',
      tips: [
        'Atanan konum, müşterinin My Stand Layout sayfasında anında görünür.',
        'Alternatif önerildiğinde müşteri e-posta ile bilgilendirilir ve 72 saat içinde yanıtlamalıdır.',
      ],
    },
    en: {
      title: 'Stand Requests',
      description:
        'Review customer stand-area requests. Approve / reject, assign locations, or propose alternatives from this page.',
      tips: [
        'Assigned locations appear instantly in the customer\'s "My Stand Layout" view.',
        'Proposed alternatives trigger an email; customers have 72 hours to respond.',
      ],
    },
  },

  'applications': {
    tr: {
      title: 'Başvurular (Applications)',
      description:
        'Yeni bekleyen kayıt başvurularını onaylayın veya reddedin. Onay verildiğinde tenant ve admin kullanıcı otomatik oluşur ve kullanıcıya e-posta gönderilir.',
      tips: [
        'Onay e-postası production URL\'si ile gönderilir; preview\'den onay verirseniz link preview\'e düşer.',
        'Red ederken "neden" alanını doldurun — ret e-postasında müşteriye iletilir.',
      ],
    },
    en: {
      title: 'Applications',
      description:
        'Approve or reject pending registration applications. On approval, the tenant and admin user are created automatically and an email is sent to the applicant.',
      tips: [
        'Approval emails use the production URL; approving from preview sends preview links.',
        'When rejecting, fill the "reason" — it\'s included in the rejection email.',
      ],
    },
  },

  'change-requests': {
    tr: {
      title: 'Değişiklik Talepleri (Change Requests)',
      description:
        'Onaylanmış stand\'ı olan müşterilerin konum / boyut değişiklik taleplerini yönetin. Yalnızca standı atayan admin bu talebi işleyebilir; Birincil Süper Admin override edebilir.',
      tips: [
        'Alternatif öneririken picker modalını kullanın; müşteri 72 saat içinde kabul / red eder.',
        'Doğrudan atama (direct mode) müşteri onayı olmadan tamamlanır — bağlı siparişler de taşınır.',
      ],
    },
    en: {
      title: 'Change Requests',
      description:
        'Manage location / size change requests from allocated tenants. Only the admin who originally assigned the stand can process; Primary Super Admin can override.',
      tips: [
        'Use the stand picker modal to propose alternatives; customers have 72 hours to respond.',
        'Direct-mode assignment skips customer approval and also migrates their orders.',
      ],
    },
  },

  'stand-designs': {
    tr: {
      title: 'Stand Tasarımları',
      description:
        'Müşterilerin yüklediği stand tasarım dosyalarını inceleyin, onay veya geri bildirim gönderin. Bu sayfadaki etkileşimler Customer Admin\'in Stand Request sekmesinde canlı görünür.',
      tips: [
        'Müşteri dosyası değiştirdiğinde alana kırmızı bir "okunmadı" rozeti eklenir.',
        'Onaylandıktan sonra müşteri ilave değişiklik yaparsa tekrar incelemeniz gerekir.',
      ],
    },
    en: {
      title: 'Stand Designs',
      description:
        'Review customer-uploaded stand design files and send approval / feedback. Conversations are reflected live in the customer\'s Stand Request tab.',
      tips: [
        'When the customer replaces a file, a red "unread" badge appears here.',
        'If they edit after approval, you\'ll need to re-review.',
      ],
    },
  },

  'orders': {
    tr: {
      title: 'Genel Siparişler',
      description:
        'Müşterilerin B2B sepet siparişlerini (tedarikçi ürünleri, modüler stand paketleri, organizatör ekstraları) yönetin. Durum değişiklikleri ve iade akışları buradan tetiklenir.',
      tips: [
        '"Paid" statüsüne geçişi şu an manuel iade (admin refund) veya ödeme mocklama üzerinden test ediyoruz; Stripe entegrasyonu (Phase 76C) yolda.',
        'Bir siparişi sildiğinizde ilgili bildirimler (notifications) otomatik temizlenir.',
      ],
    },
    en: {
      title: 'General Orders',
      description:
        'Manage customer B2B cart orders (supplier products, modular stand packages, organizer extras). Status changes and refunds flow from here.',
      tips: [
        'Transitioning to "Paid" currently relies on admin refund or mock; Stripe integration (Phase 76C) is on the way.',
        'Deleting an order auto-cleans its linked notifications.',
      ],
    },
  },

  'service-orders': {
    tr: {
      title: 'Servis Siparişleri',
      description:
        'Stand seviyesinde satın alınan servisleri (temizlik, elektrik, internet vb.) yönetin. Tedarikçi onayı gerektiren servisler için iş akışı burada ilerler.',
      tips: [
        'Servis siparişi "submitted" → "approved" → "paid" → "delivered" akışını izler.',
        'Müşteri kendi panelinde tek sipariş listesinde hem servis hem genel siparişleri görür.',
      ],
    },
    en: {
      title: 'Service Orders',
      description:
        'Manage stand-level services (cleaning, electricity, internet, etc). Supplier-confirmed services flow through here.',
      tips: [
        'Service orders follow: submitted → approved → paid → delivered.',
        'Customers see both service and general orders in a single unified list.',
      ],
    },
  },

  'sales': {
    tr: {
      title: 'Alışveriş Raporları',
      description:
        'Gerçek zamanlı satış ve sepet analitiği. Toplam ciro, ürün bazlı dağılım, tedarikçi performansı ve ödenen siparişlerin detaylı raporu burada.',
      tips: [
        '"Paid" kayıtlar ultra_password ile zorla silinebilir; bağlı bildirimler de otomatik temizlenir.',
        'Filtreler tarih aralığı ve tenant bazında sonuçları daraltır.',
      ],
    },
    en: {
      title: 'Sales Reports',
      description:
        'Real-time sales & cart analytics. Total revenue, product breakdown, supplier performance, and paid-order details live here.',
      tips: [
        'Paid records can be force-deleted with ultra_password; linked notifications are auto-purged.',
        'Filters narrow results by date range and tenant.',
      ],
    },
  },

  'messages': {
    tr: {
      title: 'Admin Mesajları',
      description:
        'Müşteri ve tedarikçilerle başlatılan birebir sohbet konuşmalarını yönetin. Okunmamış mesaj sayısı sidebar\'da rozet olarak görünür.',
      tips: [
        'Bir konuşmada yanıt verdiğinizde karşı taraf e-posta ile bilgilendirilir (günlük digest).',
        'Mesajlar 4-8 saniyelik polling ile senkronize olur; yakında WebSocket\'e geçilecek.',
      ],
    },
    en: {
      title: 'Admin Messages',
      description:
        'Manage 1-on-1 conversations with customers and suppliers. Unread counts appear as sidebar badges.',
      tips: [
        'Replies trigger a daily email digest for the other party.',
        'Messages sync via 4-8s polling; WebSocket upgrade is planned.',
      ],
    },
  },

  /* ── Stand & Halls ───────────────────────────────────────────────── */
  'blue-zone': {
    tr: {
      title: 'Stand Layoutları (Blue Zone)',
      description:
        'Her Blue Zone müşterisinin onaylanmış stand konumunu SVG grid üzerinde görüntüleyin. Hücre listesi, bounding box ve paylaşılan duvarlar burada.',
      tips: [
        'Layout "Detaylar" modalında stand alanı, yetkili kişiler ve konum doğrulanır.',
        'Bu sayfa sadece okuma amaçlıdır — değişiklik yapmak için Stand Talepleri / Değişiklik Talepleri akışını kullanın.',
      ],
    },
    en: {
      title: 'Stand Layouts (Blue Zone)',
      description:
        'View every Blue Zone tenant\'s approved stand location on an SVG grid. Cell lists, bounding box, and shared walls are surfaced here.',
      tips: [
        'Click "Details" to verify stand area, authorized persons and position.',
        'Read-only — use Stand Requests / Change Requests to modify.',
      ],
    },
  },

  'halls': {
    tr: {
      title: 'Hall Yönetimi',
      description:
        'Blue Zone hall\'larını (B-Hall 1, B-Hall 2 vb.) ve içindeki stand hücrelerini tanımlayın. m² fiyat dilimleri, grid boyutu ve hücre ızgarası buradan düzenlenir.',
      tips: [
        'Hall grid değişirse mevcut katılımcı konumları otomatik yeniden origin\'e taşınır.',
        'Her hall için ayrı fiyat dilimi tanımlanabilir (50m² altı / üstü vb).',
      ],
    },
    en: {
      title: 'Hall Management',
      description:
        'Define Blue Zone halls (B-Hall 1, B-Hall 2, …) and their stand cells. m² price tiers, grid dimensions and cell grid are edited here.',
      tips: [
        'Changing a hall grid auto-reorigins existing participant layouts.',
        'Each hall can define its own price tiers (<50m², ≥50m², …).',
      ],
    },
  },

  /* ── Catalogs ────────────────────────────────────────────────────── */
  'platform-products': {
    tr: {
      title: 'Modüler Standlar (Platform Ürünleri)',
      description:
        'Platformun kendisi tarafından sunulan hazır modüler stand paketlerini (Tier 1-4, m² bazlı) yönetin. Müşteriler bu standları Modüler Standlar sekmesinden satın alır.',
      tips: [
        'Kartlar m² değerine göre küçükten büyüğe sıralanır (Modular Packages sayfasıyla aynı düzen).',
        'Bu standlar aktif olduğunda otomatik olarak müşteri kataloğuna çıkar.',
      ],
    },
    en: {
      title: 'Modular Stands (Platform Products)',
      description:
        'Manage the platform\'s built-in modular stand packages (Tier 1-4, by m²). Customers purchase these from the Modular Stands tab.',
      tips: [
        'Cards are sorted by m² ascending (same layout as Modular Packages).',
        'Activating a stand publishes it to the customer catalog immediately.',
      ],
    },
  },

  'modular-packages': {
    tr: {
      title: 'Modüler Paketler',
      description:
        'Her modüler stand tier\'ı (25/50/100/150 m²) için paketin içine dahil organizatör ürünlerini tanımlayın. "Toplu Ekle" ile bir ürünü birden fazla pakete aynı anda ekleyebilirsiniz.',
      tips: [
        'Pakete dahil ürünlerde müşteri detay modalda "Pakete dahil" rozetini görür.',
        'Paket dışında ekstra satın almak isteyen müşteri için ürünün `extra_price` > 0 olmalı.',
      ],
    },
    en: {
      title: 'Modular Packages',
      description:
        'Define which organizer products are bundled inside each modular stand tier (25/50/100/150 m²). Use "Bulk Add" to push one product to many packages at once.',
      tips: [
        'Items in a package show an "Included in Package" badge in the customer detail modal.',
        'For optional extra purchases, the product must have extra_price > 0.',
      ],
    },
  },

  'service-catalog': {
    tr: {
      title: 'Servis Kataloğu',
      description:
        'Stand seviyesinde sunulan servisleri (ör: temizlik, elektrik, teknik destek) tanımlayın. Kategoriler, fiyatlandırma modelleri (günlük / haftalık / etkinlik) ve tedarikçi bağları burada.',
      tips: [
        'Her servis bir tedarikçiye bağlanabilir; tedarikçi onayı gerektiren servisler `requires_supplier_confirmation` flag\'ı ile işaretlenir.',
        'Servis kaldırılırsa aktif siparişler etkilenmez (snapshot alınır).',
      ],
    },
    en: {
      title: 'Service Catalog',
      description:
        'Define stand-level services (e.g. cleaning, electricity, technical support). Categories, pricing models (daily / weekly / event) and supplier bindings live here.',
      tips: [
        'Each service can bind to a supplier; those needing confirmation get the `requires_supplier_confirmation` flag.',
        'Removing a service does not affect live orders (a snapshot is taken).',
      ],
    },
  },

  'organizer-products': {
    tr: {
      title: 'Organizatör Ürünleri',
      description:
        'Organizatör tarafından yönetilen ve modüler stand paketlerine dahil edilen (veya bireysel ekstra olarak satılan) ürünler. Görseller S3 üzerinde saklanır ve atomic guard ile korunur.',
      tips: [
        'Her ürünün 1-5 görseli olabilir; ilk yüklenen kapak (cover) olarak işaretlenir.',
        'Bozuk görselleri "Bozuk Görselleri Temizle" butonu ile toplu temizleyebilirsiniz; "Medya Sağlığı" sayfası daha ince ayar sunar.',
      ],
    },
    en: {
      title: 'Organizer Products',
      description:
        'Organizer-owned products bundled inside modular stand packages (or sold as standalone extras). Images live on S3 with atomic upload-guard protection.',
      tips: [
        'Each product can have 1-5 images; the first uploaded becomes the cover.',
        'Use "Clean Broken Images" to sweep orphans; "Media Health" page offers finer control.',
      ],
    },
  },

  'supplier-products': {
    tr: {
      title: 'Tedarikçi Ürünleri (Ultra Admin görünümü)',
      description:
        'Tüm tedarikçilerin kataloglarını tek ekrandan denetleyin. Tedarikçi bazında filtreleme, aktif/pasif durum değişikliği ve görsel denetimi burada.',
      tips: [
        'Bu sayfa read-write: Ultra Admin olarak tedarikçi ürünlerini pasifleştirebilir / aktifleştirebilirsiniz.',
        'Tedarikçi onaylı siparişlerde ürün pasifleşse bile bu siparişler etkilenmez.',
      ],
    },
    en: {
      title: 'Supplier Products (Ultra Admin view)',
      description:
        'Oversee every supplier\'s catalog in one place. Filter by supplier, flip active status, and review images centrally.',
      tips: [
        'Read-write — as Ultra Admin you can de-activate supplier products.',
        'Orders already in-flight are unaffected when a product is de-activated.',
      ],
    },
  },

  'organizer-categories': {
    tr: {
      title: 'Ürün Kategorileri',
      description:
        'Organizatör ürünleri için kategori ve alt-kategori hiyerarşisini yönetin. Tüm isimler çift dilli (TR/EN) tanımlanır.',
      tips: [
        'Kategori silmeden önce bu kategoriye bağlı ürün olmadığını doğrulayın.',
        'Alt kategoriler tek bir üst kategori altında gruplandırılır ve filtrelerde görünür.',
      ],
    },
    en: {
      title: 'Product Categories',
      description:
        'Manage the category / sub-category hierarchy for organizer products. All names are bilingual (TR/EN).',
      tips: [
        'Before deleting a category, ensure no products still reference it.',
        'Sub-categories are grouped under one parent and appear in filters.',
      ],
    },
  },

  /* ── Settings sub-pages ─────────────────────────────────────────── */
  'delegate-map': {
    tr: {
      title: 'Delege Haritası',
      description:
        'UNFCCC listesine göre her ülkenin delege durumunu ve Blue Zone kayıt aşamasını harita üzerinde görün. Ülkeler rengi kayıt durumuna göre değişir.',
      tips: [
        'Test veri temizliği UNFCCC whitelist\'ini korur — listede takılı kalan ülke varsa manuel silin.',
        'Kullanıcı tenant ekledikçe harita otomatik yeşillenir.',
      ],
    },
    en: {
      title: 'Delegate Map',
      description:
        'See every country\'s UNFCCC delegate status and Blue Zone registration state on a world map. Country colors reflect registration progress.',
      tips: [
        'Test-data wipe preserves the UNFCCC whitelist — stuck countries need manual deletion.',
        'The map greens as tenants register.',
      ],
    },
  },

  'data-health': {
    tr: {
      title: 'Veri Sağlığı',
      description:
        '4 sentinel audit (cell format, money, datetime, identity) drift\'lerini tek panoda izleyin. Bu sayfa SADECE OKUR — mutate / düzeltme yapmaz. Drift root cause\'u operatörün işi.',
      tips: [
        'Haftalık digest e-posta tüm Ultra Admin\'lere gönderilir (total drift > 0 ise).',
        'Sample değerler PII maskelidir (email / telefon) — ID\'ler açıktır (audit için).',
      ],
    },
    en: {
      title: 'Data Health',
      description:
        'Surface drift alerts from 4 sentinel audits (cell format, money, datetime, identity). READ-ONLY — no mutations here. Root-cause analysis is the operator\'s job.',
      tips: [
        'Weekly digest emails are sent to all Ultra Admins when total drift > 0.',
        'Sample values are PII-masked (email / phone); IDs are shown plain for audit.',
      ],
    },
  },

  'media-health': {
    tr: {
      title: 'Medya Sağlığı (Media Health)',
      description:
        'S3 üzerinde fiziksel olarak var olan medya dosyalarını DB kayıtlarıyla karşılaştırır. "Yarı-orphan" (display yok, thumb var) ve "Tam orphan" (her ikisi de yok) kayıtları bulur. Dry-Run destekli opt-in onarım sunar.',
      tips: [
        'Yarı-orphan onarımı = display key\'i thumb\'a yeniden işaretler; görüntü kalır ama 400 px kalitede.',
        'Tam orphan onarımı = kayıt soft-delete edilir; S3\'te hiçbir şey silinmez.',
      ],
    },
    en: {
      title: 'Media Health',
      description:
        'Compares DB records against actual S3 objects. Surfaces "half-orphan" (display missing, thumb present) and "full-orphan" (both missing) records. Offers Dry-Run-first, opt-in repair.',
      tips: [
        'Half-orphan repair = rewrites display key to thumb; image remains but at 400 px quality.',
        'Full-orphan repair = soft-deletes the record; nothing is removed from S3.',
      ],
    },
  },

  'platform-rules': {
    tr: {
      title: 'Platform Kuralları',
      description:
        'Platformun iş kuralı ayarlarını düzenleyin: onay eşikleri, rezervasyon pencereleri, bildirim tetikleyicileri, para birimi ayarları.',
      tips: [
        'Kural değişiklikleri anında devreye girer — müşteri akışları etkilenebilir.',
        'Her değişiklik audit log\'a düşer.',
      ],
    },
    en: {
      title: 'Platform Rules',
      description:
        'Edit platform business-rule settings: approval thresholds, reservation windows, notification triggers, currency settings.',
      tips: [
        'Rule changes take effect immediately — customer flows may be impacted.',
        'Every change writes to the audit log.',
      ],
    },
  },

  'system-guide': {
    tr: {
      title: 'Sistem Rehberi',
      description:
        'Platformun tüm özelliklerini açıklayan kullanım dokümantasyonu. Yeni Ultra Admin\'ler için başlangıç noktası.',
      tips: [
        'Her bölüm dinamik olarak güncellenir; yeni özellik eklendikçe burada da belgelenir.',
      ],
    },
    en: {
      title: 'System Guide',
      description:
        'Platform-wide usage documentation. The starting point for any new Ultra Admin.',
      tips: [
        'Each section updates dynamically as new features land.',
      ],
    },
  },

  'super-admins': {
    tr: {
      title: 'Süper Adminler',
      description:
        'Sadece Birincil Süper Admin (is_main_user=true) bu sayfayı görür. Yeni Ultra Admin\'leri davet edin, mevcut yetkileri düşürün veya hesabı silin.',
      tips: [
        'Silme işlemi Ultra Password gerektirir; Birincil Süper Admin asla silinemez.',
        'Yeni davet gönderilen Ultra Admin\'e otomatik kurulum e-postası gider.',
      ],
    },
    en: {
      title: 'Super Admins',
      description:
        'Visible only to the Primary Super Admin (is_main_user=true). Invite new Ultra Admins, demote existing ones, or delete accounts.',
      tips: [
        'Deletion requires the Ultra Password; the Primary Super Admin is unremovable.',
        'Invited Ultra Admins receive an automatic setup email.',
      ],
    },
  },

  'stand-change-quota': {
    tr: {
      title: 'Stand Değişiklik Limiti',
      description:
        'Her müşterinin etkinlik süresince kaç defa stand yer değişikliği talep edebileceğini belirler. Varsayılan: 1. Reddedilen ve iptal edilen talepler hak tüketmez.',
      tips: [
        'Sınır 1-20 arası. Onaylanan veya bekleyen talepler hak tüketir.',
        'Müşteri sınırı aştığında "+ Yeni Talep" butonu pasifleşir ve "Hak doldu" mesajı gösterilir.',
      ],
    },
    en: {
      title: 'Stand Change Limit',
      description:
        'Sets how many stand-location change requests each tenant can submit during the event. Default: 1. Rejected & cancelled requests do NOT count.',
      tips: [
        'Range: 1-20. Approved / pending requests consume a slot.',
        'When the limit is hit, the customer\'s "+ New Request" button is disabled with a "limit reached" message.',
      ],
    },
  },

  'audit-logs': {
    tr: {
      title: 'Denetim Kayıtları',
      description:
        'Tüm yönetici eylemleri (tenant onayı, stand ataması, ürün silme, paket değişikliği vb.) için kronolojik denetim kaydı. Her eylem kim tarafından, ne zaman ve hangi hedefe uygulandığını gösterir.',
      tips: [
        'Kayıtlar asla silinmez — platform tarihsel hesap verebilirliği buradan sağlanır.',
        'Filtreler aktör / hedef tipi / zaman aralığına göre çalışır.',
      ],
    },
    en: {
      title: 'Audit Logs',
      description:
        'Chronological audit trail for every admin action (tenant approval, stand assignment, product deletion, package change, etc). Each row shows who, when and on which target.',
      tips: [
        'Logs are never deleted — this is the platform\'s long-term accountability record.',
        'Filter by actor / target type / time range.',
      ],
    },
  },
};

/**
 * Resolve the intro entry for a given page key and language.
 * Returns the canonical { title, description, tips } object that <PageIntro>
 * expects. Falls back to English if TR is missing, returns null for unknown
 * keys (page simply skips rendering the intro).
 */
export function getPageIntro(key, language = 'en') {
  const entry = REGISTRY[key];
  if (!entry) return null;
  const lang = language === 'tr' ? 'tr' : 'en';
  return entry[lang] || entry.en || null;
}

export default REGISTRY;
